import { Link } from "react-router-dom";
import { ArrowRight, Check, Clock3, Info } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Motion";
import CtaBand from "../components/CtaBand";
import { useLang } from "../lib/lang";
import { IMAGES } from "../lib/content";

export default function Classes() {
  const { t } = useLang();

  return (
    <div>
      <Seo title={t.meta.classes.title} description={t.meta.classes.desc} />

      <section className="mx-auto max-w-6xl px-5 pb-14 pt-12 sm:pt-16" data-testid="classes-hero">
        <Reveal y={12}>
          <p className="eyebrow">{t.classes.eyebrow}</p>
          <h1 className="mt-4 max-w-3xl font-display text-4xl leading-tight text-ink sm:text-5xl lg:text-6xl">
            {t.classes.title}
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-[#475569] sm:text-lg">{t.classes.sub}</p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-6xl px-5 pb-20" data-testid="classes-programs">
        <Reveal>
          <h2 className="font-display text-2xl text-ink sm:text-3xl">{t.classes.programsTitle}</h2>
        </Reveal>
        <div className="mt-8 grid gap-6 lg:grid-cols-3">
          {t.programs.cards.map((p, i) => (
            <Reveal key={i} delay={0.07 * i}>
              <article className="card-warm flex h-full flex-col p-8 hover:-translate-y-1 hover:shadow-[0_24px_50px_-24px_rgba(30,27,75,0.35)]" data-testid={`classes-card-${i}`}>
                <span className="inline-flex w-fit rounded-full bg-[#FFEDD5] px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-[#9A3412]">{p.tag}</span>
                <h3 className="mt-4 font-display text-2xl text-ink">{p.title}</h3>
                <ul className="mt-5 space-y-3">
                  {p.items.map((it, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm leading-relaxed text-[#475569]">
                      <Check size={16} className="mt-0.5 shrink-0 text-saffron" />
                      {it}
                    </li>
                  ))}
                </ul>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-cream py-20 sm:py-24" data-testid="classes-how">
        <div className="mx-auto grid max-w-6xl gap-12 px-5 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <div className="overflow-hidden rounded-[2rem] border-4 border-white shadow-[0_30px_60px_-25px_rgba(30,27,75,0.4)]">
              <img src={IMAGES.onlineClass} alt={t.classes.imageAlt} loading="lazy" className="aspect-[4/3] w-full object-cover" data-testid="classes-photo" />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <h2 className="font-display text-3xl text-ink sm:text-4xl">{t.classes.howTitle}</h2>
            <p className="mt-5 text-base leading-relaxed text-[#475569] sm:text-lg">{t.classes.howText}</p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-20 sm:py-24" data-testid="classes-timings">
        <Reveal>
          <p className="eyebrow">{t.classes.timingsEyebrow}</p>
          <h2 className="mt-4 font-display text-3xl text-ink sm:text-4xl">{t.classes.timingsTitle}</h2>
          <p className="mt-5 flex max-w-2xl items-start gap-2.5 rounded-2xl bg-[#FFEDD5]/70 p-4 text-sm leading-relaxed text-[#7C2D12]" data-testid="timings-note">
            <Info size={17} className="mt-0.5 shrink-0 text-saffron" />
            {t.classes.timingsNote}
          </p>
        </Reveal>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {t.classes.slots.map((s, i) => (
            <Reveal key={i} delay={0.06 * i}>
              <article className="card-warm flex h-full flex-col justify-between p-7 hover:-translate-y-1 hover:shadow-[0_24px_50px_-24px_rgba(30,27,75,0.35)]" data-testid={`timing-slot-${i}`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-ink text-marigold">
                  <Clock3 size={20} />
                </span>
                <div className="mt-6">
                  <h3 className="font-display text-xl text-ink">{s.name}</h3>
                  <p className={`mt-2 text-sm font-semibold text-saffron ${t === undefined ? "" : ""}`}>{s.time}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-5 pb-20 sm:pb-24" data-testid="classes-transition">
        <Reveal>
          <div className="relative mx-auto grid max-w-6xl overflow-hidden rounded-[2.5rem] bg-ink px-8 py-14 lg:grid-cols-2 lg:items-center lg:px-16">
            <div>
              <h2 className="font-display text-3xl text-cream sm:text-4xl">{t.classes.transTitle}</h2>
              <p className="mt-5 max-w-lg text-base leading-relaxed text-cream/75">{t.classes.transText}</p>
              <Link to="/contact" data-testid="transition-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-saffron px-7 py-3.5 text-sm font-semibold text-[#FDFBF7] transition-all duration-300 hover:-translate-y-0.5 hover:bg-cream hover:text-ink">
                {t.classes.transCta}
                <ArrowRight size={16} />
              </Link>
            </div>
            <div className="mt-10 lg:mt-0 lg:justify-self-end">
              <img src={IMAGES.illAkshar} alt="" loading="lazy" className="w-64 rounded-[2rem] border-4 border-cream/15 object-cover lg:w-80" />
            </div>
          </div>
        </Reveal>
      </section>

      <CtaBand />
    </div>
  );
}
